import { useCallback, useMemo } from 'react';
import { AnalyticsPixelIds, AnalyticsPixelIdsTypes } from 'core/analytics/entities';
import { evalCondition } from 'core/common/utils/evalCondition';
import { removePrimitiveDuplicatesFromArray } from 'core/common/utils/removePrimitiveDuplicatesFromArray';
import {
  FunnelAnalytics,
  isAxon,
  isBingAds,
  isFacebookPixel,
  isSnapchat,
  isTaboola,
  isTikTok,
} from 'core/funnel/entities';
import { getConditionAttributes } from 'core/funnel/store/selectors';

type UseAnalyticsIdsParameters = {
  funnelAnalytics?: FunnelAnalytics;
  funnelConditions?: ReturnType<typeof getConditionAttributes>;
};

export const useAnalyticsIds = ({
  funnelAnalytics,
  funnelConditions,
}: UseAnalyticsIdsParameters): AnalyticsPixelIds => {
  const getIds = useCallback(
    (
      analyticsGuard: (funnelAnalytics: FunnelAnalytics[number]) => boolean,
    ): AnalyticsPixelIdsTypes => {
      if (!funnelAnalytics || !Array.isArray(funnelAnalytics)) {
        return { conditional: [], nonConditional: [], combined: [] };
      }

      const analyticsObject = funnelAnalytics.find(analyticsGuard);

      if (!analyticsObject) {
        return { conditional: [], nonConditional: [], combined: [] };
      }

      const nonConditionalIds = analyticsObject.attributes
        .filter((attribute) => !attribute.condition)
        .map((attribute) => attribute.id);

      const conditionalIds = funnelConditions
        ? analyticsObject.attributes
            .filter((item) => [funnelConditions].find(evalCondition(item.condition)))
            .map((attribute) => attribute.id)
        : [];

      const combined = removePrimitiveDuplicatesFromArray([
        ...conditionalIds,
        ...nonConditionalIds,
      ]);

      return {
        conditional: removePrimitiveDuplicatesFromArray(conditionalIds),
        nonConditional: removePrimitiveDuplicatesFromArray(nonConditionalIds),
        combined,
      };
    },
    [funnelAnalytics, funnelConditions],
  );

  const facebookPixelIds = useMemo(() => getIds(isFacebookPixel), [getIds]);
  const tikTokPixelIds = useMemo(() => getIds(isTikTok), [getIds]);
  const bingAdsIds = useMemo(() => getIds(isBingAds), [getIds]);
  const taboolaIds = useMemo(() => getIds(isTaboola), [getIds]);
  const axonIds = useMemo(() => getIds(isAxon), [getIds]);
  const snapchatIds = useMemo(() => getIds(isSnapchat), [getIds]);

  return {
    facebook: facebookPixelIds,
    tikTok: tikTokPixelIds,
    bingAds: bingAdsIds,
    taboola: taboolaIds,
    axon: axonIds,
    snapchat: snapchatIds,
  };
};
